import type { DealerReportAnswer } from "@hakili/contracts";
import { ADVERTISED_STAGES } from "@hakili/domain-types";
import { formatPlate, pl, t } from "@hakili/i18n";
import {
  Button,
  Card,
  DEALER_WARN_NOTICE,
  GuardedButton,
  LIEN_TEXTE,
  METADONNEE,
  Select,
  SidePanel,
} from "@hakili/ui";
import { AlertTriangle, LayoutGrid, List, Plus, Search, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  DealerSession,
  Dealership,
  MakeReference,
  VehicleOptions,
  DoorResult,
  Inventory,
  InventoryVehicle,
  TeamMember,
  VehicleDetail,
  VehiclePhoto,
} from "../session/useSession.js";
import { type EquipmentReference, NO_EQUIPMENT } from "./equipment.js";
import { VehicleCard } from "./vehicle-card.js";
import { NO_MAKES, NO_OPTIONS, placeIsComplete, VehicleFields } from "./vehicle-fields.js";
import { PhotoSlots } from "./photo-slots.js";
import { NO_VIEWS, type PhotoViews } from "./photo-views.js";
import { EXITED_STAGES, isLive, SOLD_STAGES } from "./stages.js";

import { META_TEXT, VehicleRow } from "./vehicle-row.js";
import type { DossierRequestDoors, VehiclePaperDoors } from "../session/vehicle-papers.js";
import { HakiliFace } from "./hakili-face.js";
import { VehiclePage } from "./vehicle-page.js";
import type { DealerSettlement } from "./settlement-card.js";

/**
 * `P0-CAT-023` and `P0-DLW-010`, round I-A: **« Mes véhicules ».**
 *
 * ## The shape is the mockup's, and the addendum's where it speaks
 *
 * `hakili-dealer.jsx`'s `VehiculesScreen` gives the columns , vehicle, contact,
 * price, expertise, stage , the four filters and their stage predicates. The
 * design addendum of 5 September adds what the mockup does not have: **filters
 * that carry their count before they are clicked**, an order-by row, and on wide
 * screens **the split** , the list stays on the left while the selected car
 * opens on the right, so moving from one car to the next never leaves the list.
 *
 * **The add form is a PANEL and not the mockup's modal**, by the orders. A modal
 * over a list is a form that hides the thing it is adding to; the panel keeps
 * the inventory visible while a car is typed into it.
 *
 * ## The counts are DERIVED from the door's answer
 *
 * Every filter's count is computed from the rows the api returned, by the same
 * predicate that filters them. A count fetched separately would be a second
 * question with a second answer, and the two would disagree the moment a car
 * moved stage between the requests.
 *
 * ## What is deliberately empty
 *
 * The **seller grade** is `partnerships`' computed figure and does not exist:
 * the slot renders nothing rather than a plausible letter (`A48`). The **photo**
 * is `I-B`'s: a placeholder frame stands where it will go, and a car may be
 * listed without one , a flagged divergence from `WF-DLR-02`, closed by `I-B`.
 */

/** The mockup's own predicates, kept verbatim so the two screens agree. */
// The same two sets `stages.ts` holds, read rather than restated , the filters
// and the dashboard's counters must agree about what « sold » means.

type FilterId = "active" | "sold" | "exited" | "all";
type SortId = "recent" | "prixAsc" | "prixDesc" | "kilometrage" | "etape";

const KEEPS: Readonly<Record<FilterId, (stage: string) => boolean>> = {
  active: (stage) => isLive(stage),
  sold: (stage) => SOLD_STAGES.has(stage),
  exited: (stage) => EXITED_STAGES.has(stage),
  all: () => true,
};

const ADVERTISED: ReadonlySet<string> = new Set(ADVERTISED_STAGES);
/** `C-D1`: « Privées », the cars advertised-stage and not yet published, as the row's own « Non publié » chip says it. */
const isPrivate = (row: InventoryVehicle): boolean => ADVERTISED.has(row.stage) && !row.published;

const FILTERS: readonly { readonly id: FilterId; readonly cle: string }[] = [
  { id: "active", cle: "dealer.vehicules.filtreActifs" },
  { id: "sold", cle: "dealer.vehicules.filtreVendus" },
  { id: "exited", cle: "dealer.vehicules.filtreSortis" },
  { id: "all", cle: "dealer.vehicules.filtreTous" },
];

const SORTS: readonly { readonly id: SortId; readonly cle: string }[] = [
  { id: "recent", cle: "dealer.vehicules.triRecent" },
  { id: "prixAsc", cle: "dealer.vehicules.triPrixAsc" },
  { id: "prixDesc", cle: "dealer.vehicules.triPrixDesc" },
  { id: "kilometrage", cle: "dealer.vehicules.triKilometrage" },
  // Not in the drawing, and kept. The drawing's fifth control is « Suivi par »,
  // a filter on the person following the car , a fact no door carries , and
  // dropping a working order for a control that could only be drawn dead would
  // be trading something for nothing. Flagged beside the mockup.
  { id: "etape", cle: "dealer.vehicules.triEtape" },
];

/**
 * The stage vocabulary moved to `stages.ts` at `round F`, `F2`.
 *
 * **It lived here AND in `vehicles.tsx`, character for character**, and the
 * dashboard would have made a third copy. All three carried the same eight
 * entries of an eleven-value enum, the same label for a `reserve` stage the api
 * cannot send, and the same four missing , so the same defect was present three
 * times and fixable in none of them. `P1-UIK-027`'s rule: the lists may differ,
 * the vocabulary may not.
 */

export interface VehiclesProps {
  readonly listVehicles: () => Promise<DoorResult<Inventory>>;
  /** `F2.2`'s doors, passed down like every other , the screen holds no client. */
  readonly listPhotos: (
    vehicleId: string,
  ) => Promise<DoorResult<{ photos: readonly VehiclePhoto[]; missingRecommended: readonly string[] }>>;
  readonly uploadPhoto: (input: {
    vehicleId: string;
    view: string;
    file: File;
  }) => Promise<DoorResult<{ id: string }>>;
  readonly removePhoto: (vehicleId: string, photoId: string) => Promise<DoorResult<void>>;
  /** `A-DLR`, Arc A: the vehicle's own papers, handed to the car's page. */
  readonly papers?: VehiclePaperDoors | undefined;
  /** `A-DLR`, Arc B: « Pièces demandées », handed to the car's page. */
  readonly requests?: DossierRequestDoors | undefined;
  readonly publishVehicle: (vehicleId: string) => Promise<DoorResult<{ publishedAt: string }>>;
  /** `F2.3`: the view vocabulary, from the api that owns it. */
  readonly listPhotoViews: () => Promise<DoorResult<PhotoViews>>;
  /** `F2`: the equipment suggestions and the bounds the api enforces. */
  readonly listEquipment: () => Promise<DoorResult<EquipmentReference>>;
  readonly readVehicle: (id: string) => Promise<DoorResult<VehicleDetail>>;
  readonly createVehicle: (input: Record<string, unknown>) => Promise<DoorResult<{ id: string }>>;
  readonly nextReference: () => Promise<DoorResult<string>>;
  readonly readDealership: () => Promise<DoorResult<Dealership>>;
  readonly listCities: () => Promise<DoorResult<readonly { id: string; name: string }[]>>;
  readonly listCityZones: (cityId: string) => Promise<DoorResult<readonly { code: string; label: string }[]>>;
  /** `round F4`: the reference the two forms type against, and the four changes. */
  readonly listVehicleMakes: () => Promise<DoorResult<MakeReference>>;
  readonly listVehicleOptions: () => Promise<DoorResult<VehicleOptions>>;
  readonly editVehicle: (
    id: string,
    input: Record<string, unknown>,
  ) => Promise<DoorResult<{ changed: boolean }>>;
  readonly changePrice: (
    id: string,
    priceAmount: number,
  ) => Promise<DoorResult<{ priceAmount: number; previousAmount: number }>>;
  readonly withdrawVehicle: (id: string) => Promise<DoorResult<{ stage: string }>>;
  /**
   * `round F6`, `OD-15`: the manager hands a car to a colleague.
   *
   * Optional, and its absence is the rule: an employee's app passes nothing, so
   * the fiche renders « Suivi par » with no « Changer » , and the door refuses
   * him in its own words if he ever reaches it another way.
   */
  readonly assignVehicle?: (
    id: string,
    userId: string,
  ) => Promise<DoorResult<{ id: string; fullName: string }>>;
  /** The team, for the « Suivi par » control and for the row's own filter. */
  readonly listTeam?: () => Promise<DoorResult<readonly TeamMember[]>>;
  readonly relistVehicle: (id: string) => Promise<DoorResult<{ stage: string }>>;
  /** `T-2` (WF-DLR-11): the settlement's doors, handed to the car's page. */
  readonly readSettlements?: () => Promise<DoorResult<readonly DealerSettlement[]>>;
  readonly confirmReceipt?: (settlementId: string, receivedOn: string) => Promise<DoorResult<unknown>>;
  readonly reportNonReceipt?: (settlementId: string) => Promise<DoorResult<unknown>>;
  readonly handoverVehicle?: (vehicleId: string, voucherReference: string) => Promise<DoorResult<unknown>>;
  /** `T-2b`, `M1`: the dealer's step at the control. */
  readonly readAvailability?: (vehicleId: string) => Promise<DoorResult<unknown>>;
  readonly readReport?: (vehicleId: string) => Promise<DoorResult<DealerReportAnswer>>;
  readonly confirmAvailability?: (vehicleId: string) => Promise<DoorResult<unknown>>;
  /** `PRICE-GAP-DEALER`, M1: the gap on the car's open file, and the seller's three answers to it. */
  readonly readPriceGap?: DealerSession["readPriceGap"];
  readonly answerPriceGap?: DealerSession["answerPriceGap"];
  /** Set when the app is on `/vehicules/:id`: the page is its own route on narrow screens. */
  readonly selectedId?: string;
  readonly naviguer?: (to: string) => void;
}

export function Vehicles({
  listVehicles,
  listPhotoViews,
  listEquipment,
  listPhotos,
  uploadPhoto,
  removePhoto,
  papers,
  requests,
  publishVehicle,
  readVehicle,
  createVehicle,
  nextReference,
  readDealership,
  listVehicleMakes,
  listVehicleOptions,
  editVehicle,
  changePrice,
  withdrawVehicle,
  relistVehicle,
  readSettlements,
  confirmReceipt,
  reportNonReceipt,
  handoverVehicle,
  readAvailability,
  readReport,
  confirmAvailability,
  readPriceGap,
  answerPriceGap,
  listCities,
  listCityZones,
  assignVehicle,
  listTeam,
  selectedId,
  naviguer,
}: VehiclesProps): React.JSX.Element {
  const [rows, setRows] = useState<readonly InventoryVehicle[] | null>(null);
  /**
   * The SERVER's instant, from the list door.
   *
   * `OD-17` as ruled: no front application owns a clock. Held in state beside
   * the rows because it belongs to the same answer , a instant from one read
   * and rows from another would compute an age between two different moments.
   */
  const [asOf, setAsOf] = useState("");
  /**
   * The cover photograph per car, for the list's thumbnails.
   *
   * A map and not a field on the row: the URL is signed at read time and never
   * stored, so it is not the car's property , it is this render's.
   */
  const [covers, setCovers] = useState<Readonly<Record<string, string>>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<FilterId>("active");
  /** `C-D1`: cards by default (the iteration-2 frames), the table one press away; the tiles narrow to the private cars. */
  const [vue, setVue] = useState<"cartes" | "tableau">("cartes");
  const [prives, setPrives] = useState(false);
  const [tri, setTri] = useState<SortId>("recent");
  /**
   * `round F6`, `OD-24`: whose cars the list is showing. `tous` is everybody's,
   * which is the state a dealership opens on.
   */
  const [suiviPar, setSuiviPar] = useState("tous");
  const [team, setTeam] = useState<readonly TeamMember[]>([]);

  /**
   * `round F6`: the team, read ONCE for the filter and for the fiche's own
   * control. `OD-10`: both rungs may read it, so this is not behind a right.
   *
   * A dealership of one has nothing to filter by, so the control renders only
   * when there is somebody to choose , a select with one option is furniture.
   */
  useEffect(() => {
    if (listTeam === undefined) return;
    void listTeam().then((answer) => {
      if (answer.ok) setTeam(answer.value.filter((member) => member.status === "actif"));
    });
  }, [listTeam]);
  const [ouvert, setOuvert] = useState<string | null>(selectedId ?? null);
  /**
   * `A2`: the car's page REPLACES the list, so the route is the way back too: « Mes véhicules » in the rail, or the browser's back,
   * moves `selectedId` to nothing and the list must return (the split view kept the list on screen and needed none of this).
   */
  useEffect(() => {
    setOuvert(selectedId ?? null);
  }, [selectedId]);
  const [ajout, setAjout] = useState(false);
  /**
   * The addendum's search box. It filters what the door already answered.
   *
   * A search that called the api on each keystroke would be a second question
   * whose answer can disagree with the counts beside it , the same reason the
   * filter counts are derived from these rows rather than fetched.
   */
  const [recherche, setRecherche] = useState("");

  const charger = useCallback(async (): Promise<void> => {
    const answer = await listVehicles();
    if (answer.ok) {
      setRows(answer.value.vehicles);
      setAsOf(answer.value.asOf);
      setErreur(null);
    } else {
      setErreur(answer.message);
    }
  }, [listVehicles]);

  /**
   * The cover of every car that has one, read once per list.
   *
   * **Only for cars whose `photoCount` is above zero**, so a forecourt of
   * unphotographed cars costs no requests at all , the count comes with the
   * list and is the cheapest possible way to know.
   */
  useEffect(() => {
    let alive = true;
    const withPhotos = (rows ?? []).filter((row) => row.photoCount > 0);
    void Promise.all(
      withPhotos.map(async (row) => {
        const answer = await listPhotos(row.id);
        return answer.ok ? ([row.id, answer.value.photos[0]?.url ?? ""] as const) : null;
      }),
    ).then((found) => {
      if (!alive) return;
      setCovers(
        Object.fromEntries(found.filter((entry) => entry !== null && entry[1] !== "") as [string, string][]),
      );
    });
    return () => {
      alive = false;
    };
  }, [rows, listPhotos]);

  useEffect(() => {
    void charger();
  }, [charger]);

  useEffect(() => {
    if (selectedId !== undefined) setOuvert(selectedId);
  }, [selectedId]);

  /**
   * The counts, derived from the SAME rows the list renders and by the SAME
   * predicate that filters them. A separate count endpoint would be a second
   * question, and the two answers would disagree the moment a car moved stage
   * between the requests.
   */
  const comptes = useMemo((): Readonly<Record<FilterId, number>> => {
    const all = rows ?? [];
    return {
      active: all.filter((row) => KEEPS.active(row.stage)).length,
      sold: all.filter((row) => KEEPS.sold(row.stage)).length,
      exited: all.filter((row) => KEEPS.exited(row.stage)).length,
      all: all.length,
    };
  }, [rows]);

  const visibles = useMemo((): readonly InventoryVehicle[] => {
    const q = recherche.trim().toLocaleLowerCase("fr-FR");
    const kept = (rows ?? [])
      .filter((row) => KEEPS[filtre](row.stage))
      .filter((row) => !prives || isPrivate(row))
      /**
       * `round F6`, `OD-24`: **« Suivi par », the drawing's fifth control.**
       *
       * A FILTER and not a sort, which is why it stands beside « Étape » rather
       * than replacing it: the drawing's own row carries both. `tous` is the
       * default and matches everything, including the cars that follow nobody ,
       * a dealership must be able to see its whole parc without choosing a
       * person first.
       */
      .filter((row) => suiviPar === "tous" || row.assigneeUserId === suiviPar)
      /**
       * The drawing searches marque, référence, immatriculation and acheteur.
       * **Three of the four are matched**; the fourth is a buyer's NAME, which
       * no door puts on a car , the row carries only `hasBuyer` (06 M3: a
       * dealer is told THAT, not WHO) , so the placeholder does not promise it either.
       *
       * **The plate is matched on its NORMALISED form, and on the spelling the
       * record shows, and on what a person actually types.** `formatPlate`
       * renders « AB 1042 MD » while the column stores it normalised, and a
       * seller reading a windscreen types « ab1042md » as often as not. All
       * three answer, because a search that matched only one of them is a
       * search that works for whoever wrote it.
       */
      .filter((row) => {
        if (q === "") return true;
        // `round F4`: the search matches the plate WHEN THERE IS ONE. A car
        // with none contributes nothing here rather than the word « null ».
        const plate = row.plate ?? "";
        const haystack = `${row.title} ${row.stockReference} ${plate} ${formatPlate(plate)}`;
        const flat = haystack.replaceAll(/[\s-]+/gu, "");
        return (
          haystack.toLocaleLowerCase("fr-FR").includes(q) ||
          flat.toLocaleLowerCase("fr-FR").includes(q.replaceAll(/[\s-]+/gu, ""))
        );
      });
    const ordered = [...kept];
    ordered.sort((left, right) => {
      if (tri === "prixAsc") return left.priceAmount - right.priceAmount;
      if (tri === "prixDesc") return right.priceAmount - left.priceAmount;
      if (tri === "kilometrage") return left.mileageKm - right.mileageKm;
      if (tri === "etape") return left.stage.localeCompare(right.stage);
      return right.listedOn.localeCompare(left.listedOn);
    });
    return ordered;
  }, [rows, filtre, tri, recherche, suiviPar, prives]);

  const ouvrir = (id: string): void => {
    setOuvert(id);
    // The page stays its own ROUTE, so a deep link keeps working and the split
    // is a rendering choice rather than a second addressing scheme.
    naviguer?.(`/vehicules/${id}`);
  };

  if (rows === null && erreur === null) {
    return (
      <Card>
        <p className="text-sm text-gray-500" data-testid="vehicules-chargement">
          {t("common.state.chargement")}
        </p>
      </Card>
    );
  }

  return (
    // The addendum's own root (`hakili-dealer.jsx:1652-1673`): a header block
    // that does not scroll, then two columns that scroll separately.
    <main className="flex min-w-0 flex-1 flex-col overflow-hidden" data-testid="vehicules">
      <div className={`shrink-0 border-b border-gray-200 px-8 pt-7 pb-4 ${ouvert === null ? "" : "hidden"}`}>
        <div className="flex items-start justify-between gap-4" data-testid="vehicules-entete">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t("dealer.vehicules.titre")}</h1>
            <p className="mt-1 text-sm text-gray-500">{t("dealer.vehicules.intro")}</p>
          </div>
          {/* `R68`: one unbreakable token. With the panel open the header lost
              its room and « Ajouter un véhicule » wrapped onto three lines.
              The header is now above BOTH columns and takes its width from the
              screen, so the panel cannot squeeze it at all. */}
          {/* `round F5`, to the pair: the drawing puts a `+` on it
              (`hakili-dealer.jsx`, « Mes véhicules »), as it does on « Inviter
              un employé ». One glyph, and the two primary creations in this app
              look like the same kind of act. */}
          <Button
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap"
            onClick={() => {
              setAjout(true);
            }}
            data-testid="vehicules-ajouter"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t("dealer.vehicules.ajouter")}
          </Button>
        </div>

        {/* `C-D1`: the two tiles the rows can state (the frames' « Prêtes à financer », « À préparer » and « Ne bougent pas » wait for served fields). Pressing « Privées » narrows to the private cars. */}
        <div
          className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
          data-testid="vehicules-tuiles"
        >
          <button
            type="button"
            aria-pressed={!prives}
            onClick={() => {
              setPrives(false);
            }}
            className={`rounded-xl border bg-white px-4 py-3 text-left ${prives ? "border-gray-200" : "border-indigo-600"}`}
            data-testid="vehicules-tuile-toutes"
          >
            <span className="block text-2xl font-semibold tabular-nums text-gray-900">
              {String((rows ?? []).length)}
            </span>
            <span className="block text-sm font-medium text-gray-900">
              {t("dealer.vehicules.tuileToutes")}
            </span>
            <span className={`block ${METADONNEE} ${META_TEXT}`}>
              {t("dealer.vehicules.tuileToutesSous")}
            </span>
          </button>
          <button
            type="button"
            aria-pressed={prives}
            onClick={() => {
              setPrives(true);
            }}
            className={`rounded-xl border bg-white px-4 py-3 text-left ${prives ? "border-indigo-600" : "border-gray-200"}`}
            data-testid="vehicules-tuile-privees"
          >
            <span className="block text-2xl font-semibold tabular-nums text-gray-900">
              {String((rows ?? []).filter(isPrivate).length)}
            </span>
            <span className="block text-sm font-medium text-gray-900">
              {t("dealer.vehicules.tuilePrivees")}
            </span>
            <span className={`block ${METADONNEE} ${META_TEXT}`}>
              {t("dealer.vehicules.tuilePriveesSous")}
            </span>
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="vehicules-filtres">
          {FILTERS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => {
                setFiltre(entry.id);
              }}
              aria-pressed={filtre === entry.id}
              className={
                "rounded-lg border px-3 py-2 text-xs font-medium tabular-nums " +
                (filtre === entry.id
                  ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50")
              }
              data-testid={`vehicules-filtre-${entry.id}`}
            >
              {/* `A2` (D2 12): a failed read prints the repo's dash, never « 0 »: the counts are masked rather than false. */}
              {t(entry.cle)} · {rows === null ? t("common.valeur.tiret") : comptes[entry.id]}
            </button>
          ))}

          {/* The search the addendum puts on this row. It filters the PARK the
              door already answered, never the door: one read, filtered in the
              hand, so a keystroke can never become a request. */}
          <div className="ml-auto flex w-72 items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-2">
            {/* Inside the field, as the drawing has it (`:1595`). Outside it,
              or absent, the box reads as one more text input on a row that
              already has five controls. */}
            <Search className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
            <input
              value={recherche}
              onChange={(event) => {
                setRecherche(event.target.value);
              }}
              placeholder={t("dealer.vehicules.rechercherIndice")}
              aria-label={t("dealer.vehicules.rechercher")}
              className="flex-1 bg-white text-xs outline-none placeholder:text-[color:var(--hk-text-2)]"
              data-testid="vehicules-recherche"
            />
            {recherche === "" ? null : (
              <button
                type="button"
                onClick={() => {
                  setRecherche("");
                }}
                className="text-xs text-[color:var(--hk-text-2)] hover:text-gray-900"
                data-testid="vehicules-recherche-vider"
              >
                {t("dealer.vehicules.vider")}
              </button>
            )}
          </div>
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="vehicules-tri">
          <span className="text-xs text-[color:var(--hk-text-2)]">{t("dealer.vehicules.trierPar")}</span>
          {SORTS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => {
                setTri(entry.id);
              }}
              aria-pressed={tri === entry.id}
              className={
                "rounded-lg border px-2.5 py-1.5 text-xs font-medium " +
                (tri === entry.id
                  ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                  : "border-gray-200 bg-white text-gray-600")
              }
              data-testid={`vehicules-tri-${entry.id}`}
            >
              {t(entry.cle)}
            </button>
          ))}

          {/**
           * `round F6`, closing `OD-24`: **« Suivi par », the fifth control of
           * the drawing's order-by row.**
           *
           * The decision was opened because the drawing's fifth control filters
           * on a fact no door carried , the build kept « Étape », which sorts on
           * something every row already has. `OD-15` gave the fact a column and
           * both doors now send it, so the control is built as the drawing
           * draws it (`hakili-dealer.jsx:1629-1650`) and « Étape » stays beside
           * it: one sorts, the other filters, and the drawing's own row has
           * both.
           *
           * **Both rungs see it.** `OD-10` closed on the founder's words , an
           * employee sees his team read-only , and a filter is a reading. What
           * he cannot do is hand a car over, which is a different control on a
           * different screen and refused by a different right.
           */}
          {team.length === 0 ? null : (
            <>
              <span className={`ml-3 flex items-center gap-1.5 ${METADONNEE} ${META_TEXT}`}>
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                {t("dealer.vehicules.suiviPar")}
              </span>
              <span data-testid="vehicules-suivi">
                <Select
                  id="vehicules-suivi"
                  value={suiviPar}
                  onChange={(value) => {
                    setSuiviPar(value);
                  }}
                  options={[
                    { value: "tous", label: t("dealer.vehicules.suiviTous") },
                    ...team.map((membre) => ({ value: membre.id, label: membre.fullName })),
                  ]}
                />
              </span>
            </>
          )}
        </div>

        {/* `C-D1`: how many cars are shown, and the two views (cards by default, the table one press away). */}
        <div className="mt-3 flex items-center justify-between gap-3" data-testid="vehicules-presentation">
          <p className={`${METADONNEE} ${META_TEXT}`} data-testid="vehicules-compte">
            {pl(visibles.length, t("dealer.vehicules.compteUn"), t("dealer.vehicules.comptePlusieurs"))}
          </p>
          <div
            role="group"
            aria-label={t("dealer.vehicules.vueLabel")}
            className="inline-flex overflow-hidden rounded-lg border border-gray-200"
          >
            <button
              type="button"
              aria-pressed={vue === "cartes"}
              onClick={() => {
                setVue("cartes");
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${vue === "cartes" ? "bg-indigo-600 text-white" : "bg-white text-gray-700"}`}
              data-testid="vehicules-vue-cartes"
            >
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />
              {t("dealer.vehicules.vueCartes")}
            </button>
            <button
              type="button"
              aria-pressed={vue === "tableau"}
              onClick={() => {
                setVue("tableau");
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${vue === "tableau" ? "bg-indigo-600 text-white" : "bg-white text-gray-700"}`}
              data-testid="vehicules-vue-tableau"
            >
              <List className="h-3.5 w-3.5" aria-hidden="true" />
              {t("dealer.vehicules.vueTableau")}
            </button>
          </div>
        </div>

        {erreur === null || rows === null ? null : (
          <p role="alert" className="mt-3 text-sm text-rose-600" data-testid="vehicules-erreur">
            {erreur}
          </p>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {ouvert !== null ? (
          /* `A2` (D2 13): a car's page is its OWN full-width screen, no split; « Mes véhicules » brings the table back. It is the same
             component the `/vehicules/:id` route renders, so there is one vehicle page and not two. */
          <div className="min-w-0 flex-1 px-6 py-4" data-testid="vehicules-volet">
            <button
              type="button"
              onClick={() => {
                setOuvert(null);
                naviguer?.("/vehicules");
              }}
              className="mb-3 text-sm text-gray-500 hover:text-gray-900"
              data-testid="vehicule-retour"
            >
              ‹ {t("dealer.vehicules.titre")}
            </button>
            <VehiclePage
              id={ouvert}
              readVehicle={readVehicle}
              listPhotos={listPhotos}
              removePhoto={removePhoto}
              papers={papers}
              requests={requests}
              listPhotoViews={listPhotoViews}
              uploadPhoto={uploadPhoto}
              publishVehicle={publishVehicle}
              editVehicle={editVehicle}
              changePrice={changePrice}
              withdrawVehicle={withdrawVehicle}
              relistVehicle={relistVehicle}
              {...(readSettlements === undefined ? {} : { readSettlements })}
              {...(confirmReceipt === undefined ? {} : { confirmReceipt })}
              {...(reportNonReceipt === undefined ? {} : { reportNonReceipt })}
              {...(handoverVehicle === undefined ? {} : { handoverVehicle })}
              {...(readAvailability === undefined ? {} : { readAvailability })}
              {...(readReport === undefined ? {} : { readReport })}
              {...(confirmAvailability === undefined ? {} : { confirmAvailability })}
              {...(readPriceGap === undefined ? {} : { readPriceGap })}
              {...(answerPriceGap === undefined ? {} : { answerPriceGap })}
              {...(naviguer === undefined ? {} : { naviguer })}
              listVehicleMakes={listVehicleMakes}
              listVehicleOptions={listVehicleOptions}
              listEquipment={listEquipment}
              listCities={listCities}
              listCityZones={listCityZones}
              {...(assignVehicle === undefined ? {} : { assignVehicle })}
              {...(listTeam === undefined ? {} : { listTeam })}
              onChanged={() => {
                // The list is the other half of the same fact: a withdrawal, a relist or a new price changes the row as much as the page.
                void charger();
              }}
            />
          </div>
        ) : rows === null ? (
          /* `A2` (D2 12): a failed read says so, and says nothing was lost; the counts above are dashes, and no « aucun véhicule » is claimed. */
          <div className="px-8 py-4" data-testid="vehicules-lecture-echec">
            {/* One pattern for all platforms (the architect's ruling of 31 October 2026, the bank's D2 frame 210): the warn notice, the alert icon
                left of the title, « Réessayer » the kit's SECONDARY BUTTON inside it. The words stay D2's (state 12, approved). */}
            <div
              role="alert"
              className={`flex items-start gap-3 rounded-xl border ${DEALER_WARN_NOTICE} px-4 py-3 text-sm`}
            >
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="m-0 font-semibold text-gray-900">{t("dealer.vehicules.lectureEchecTitre")}</p>
                <p className="m-0 mt-1 text-gray-700">{t("dealer.vehicules.lectureEchecCorps")}</p>
                <div className="mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      void charger();
                    }}
                    data-testid="vehicules-reessayer"
                  >
                    {t("common.action.reessayer")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-8 py-4" data-testid="vehicules-liste">
            {visibles.length === 0 ? (
              // In WORDS. An empty list reads as a screen that failed; a sentence reads as a filter that matched nothing, and « nothing
              // matched your search » and « nothing is under this filter » are different facts about the forecourt.
              <div
                className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500"
                data-testid="vehicules-vide"
              >
                {rows.length === 0 ? (
                  /* D2 11: the empty park, and the first car one gesture away (the privé inventory arrives with its door). */
                  <>
                    <HakiliFace />
                    <p className="m-0 font-semibold text-gray-900">{t("dealer.vehicules.videTitre")}</p>
                    <p className="m-0 mt-1">{t("dealer.vehicules.videCorps")}</p>
                    <p className="m-0 mt-1">{t("dealer.vehicules.videCorps2")}</p>
                  </>
                ) : recherche.trim() === "" ? (
                  t("dealer.vehicules.aucun")
                ) : (
                  t("dealer.vehicules.aucuneCorrespondance")
                )}
              </div>
            ) : vue === "cartes" ? (
              <div
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                data-testid="vehicules-cartes"
              >
                {visibles.map((row) => (
                  <VehicleCard
                    key={row.id}
                    vehicle={row}
                    asOf={asOf}
                    {...(covers[row.id] === undefined ? {} : { coverUrl: covers[row.id] })}
                    {...(row.published
                      ? {}
                      : {
                          onPublish: () => {
                            void publishVehicle(row.id).then((answer) => {
                              if (answer.ok) void charger();
                              else setErreur(answer.message);
                            });
                          },
                        })}
                    onOpen={() => {
                      ouvrir(row.id);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                      <th className="px-4 py-2.5">{t("dealer.vehicules.colVehicule")}</th>
                      <th className="px-4 py-2.5 text-right">{t("dealer.vehicules.colPrix")}</th>
                      <th className="px-4 py-2.5">{t("dealer.vehicules.colEtat")}</th>
                      <th className="px-4 py-2.5">{t("dealer.vehicules.colRapport")}</th>
                      <th className="px-4 py-2.5">
                        <span className="sr-only">{t("dealer.vehicules.colAction")}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map((row) => (
                      <VehicleRow
                        key={row.id}
                        vehicle={row}
                        asOf={asOf}
                        {...(covers[row.id] === undefined ? {} : { coverUrl: covers[row.id] })}
                        {...(row.published
                          ? {}
                          : {
                              // `R76`: the gesture on the row, and ONLY where there is one gesture with nothing to arbitrate.
                              onPublish: () => {
                                void publishVehicle(row.id).then((answer) => {
                                  if (answer.ok) void charger();
                                  else setErreur(answer.message);
                                });
                              },
                            })}
                        onOpen={() => {
                          ouvrir(row.id);
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <SidePanel
        open={ajout}
        title={t("dealer.vehicules.ajouter")}
        subtitle={t("dealer.vehicules.ajouterSous")}
        closeLabel={t("common.action.fermer")}
        onClose={() => {
          setAjout(false);
        }}
      >
        <AddVehicle
          {...(naviguer === undefined ? {} : { naviguer })}
          listPhotoViews={listPhotoViews}
          listEquipment={listEquipment}
          listVehicleMakes={listVehicleMakes}
          listVehicleOptions={listVehicleOptions}
          uploadPhoto={uploadPhoto}
          publishVehicle={publishVehicle}
          createVehicle={createVehicle}
          nextReference={nextReference}
          readDealership={readDealership}
          listCities={listCities}
          listCityZones={listCityZones}
          onListed={() => {
            setAjout(false);
            void charger();
          }}
          onCancel={() => {
            setAjout(false);
            /**
             * `round F3`, `W2`: **the list is re-read on the way out.**
             *
             * A panel closed after a failed photograph leaves a real car , the
             * creation succeeded , and the founder's `V-002` and `V-003` are
             * exactly that. If the list is not re-read the car is invisible
             * until the next navigation, and a person who cannot see it
             * presses « Ajouter un véhicule » again, which is how two rows
             * become four.
             */
            void charger();
          }}
        />
      </SidePanel>
    </main>
  );
}

interface AddVehicleProps {
  readonly createVehicle: (input: Record<string, unknown>) => Promise<DoorResult<{ id: string }>>;
  /** `F2.2`'s upload, one call per view, from this panel. */
  readonly uploadPhoto: (input: {
    vehicleId: string;
    view: string;
    file: File;
  }) => Promise<DoorResult<{ id: string }>>;
  readonly publishVehicle: (vehicleId: string) => Promise<DoorResult<{ publishedAt: string }>>;
  /** `F2.3`: the view vocabulary, from the api that owns it. */
  readonly listPhotoViews: () => Promise<DoorResult<PhotoViews>>;
  /** `F2`: the equipment suggestions and the bounds the api enforces. */
  readonly listEquipment: () => Promise<DoorResult<EquipmentReference>>;
  readonly nextReference: () => Promise<DoorResult<string>>;
  readonly readDealership: () => Promise<DoorResult<Dealership>>;
  readonly listCities: () => Promise<DoorResult<readonly { id: string; name: string }[]>>;
  readonly listCityZones: (cityId: string) => Promise<DoorResult<readonly { code: string; label: string }[]>>;
  /** `P0-CAT-030`: the makes and their models, and the gearbox and the fuel. */
  readonly listVehicleMakes: () => Promise<DoorResult<MakeReference>>;
  readonly listVehicleOptions: () => Promise<DoorResult<VehicleOptions>>;
  readonly onListed: () => void;
  readonly onCancel: () => void;
  /**
   * `FX-2a`, `M1`, second reading: **the way to Profil, from the control that is
   * waiting on it.**
   *
   * Optional, and the line renders without it: the sentence is the finding's cure
   * and the LINK is its convenience, so a composition that cannot navigate still
   * says why the gesture waits.
   */
  readonly naviguer?: (to: string) => void;
}

/**
 * The add form. **A panel, and its city and quartier are PREFILLED.**
 *
 * `P0-PTA-001` put the dealership's address on `partner_accounts`; this reads it
 * and fills the two fields, editable per car , a dealership sells cars that are
 * mostly where it is and sometimes not. Prefilled and editable is the shape that
 * matches both, and typing the city thirty times is how a dealership ends up
 * with three spellings of Hamdallaye.
 *
 * The stock reference is proposed as the next in the dealer's own series and is
 * editable for the same reason.
 */
function AddVehicle({
  createVehicle,
  listPhotoViews,
  listEquipment,
  uploadPhoto,
  publishVehicle,
  nextReference,
  readDealership,
  listCities,
  listCityZones,
  listVehicleMakes,
  listVehicleOptions,
  onListed,
  onCancel,
  naviguer,
}: AddVehicleProps): React.JSX.Element {
  const [champs, setChamps] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [cities, setCities] = useState<readonly { id: string; name: string }[]>([]);
  const [places, setPlaces] = useState<readonly { code: string; label: string }[]>([]);

  useEffect(() => {
    void (async () => {
      const [reference, dealership, offered] = await Promise.all([
        nextReference(),
        readDealership(),
        listCities(),
      ]);
      if (offered.ok) setCities(offered.value);
      /**
       * `round F3`: **a proposal fills a field only while it is UNTOUCHED.**
       *
       * These three answers arrive after the panel has opened, and a person
       * types as soon as it does. The first cut wrote them in regardless, so a
       * reference typed in the first second was silently replaced by the
       * door's , the walk of `upload-walk.spec.ts` met it and had to be
       * written around it, and a seller would simply have lost what he typed
       * without ever knowing why.
       *
       * `current[key] === undefined` is the test and not `=== ""`: a field a
       * person has emptied ON PURPOSE is a field they have touched, and
       * refilling it would be the same defect wearing a different value.
       */
      setChamps((current) => {
        const untouched = (key: string): boolean => current[key] === undefined;
        return {
          ...current,
          ...(reference.ok && untouched("stockReference") ? { stockReference: reference.value } : {}),
          ...(dealership.ok && untouched("city") && untouched("district")
            ? { city: dealership.value.city ?? "", district: dealership.value.district ?? "" }
            : {}),
        };
      });
    })();
  }, [nextReference, readDealership, listCities]);

  /**
   * `R64`, rule three: **the quartier never overrides the city.** Inside Bamako
   * it is chosen among the city's own places; outside, the city has one zone
   * and the quartier is free text. Either way the CITY decides, and the list a
   * person sees is the list of that city.
   */
  /**
   * `P0-CAT-030`: the makes, their models, the gearbox and the fuel.
   *
   * Read from the api that owns them, never transcribed , the same rule the
   * photograph views follow, and for a stronger reason: these names go into
   * columns a buyer searches on, so a screen holding its own copy would be a
   * screen inventing the vocabulary of the catalogue.
   */
  const [makes, setMakes] = useState<MakeReference>(NO_MAKES);
  const [options, setOptions] = useState<VehicleOptions>(NO_OPTIONS);

  useEffect(() => {
    const mounted = { yes: true };
    void Promise.all([listVehicleMakes(), listVehicleOptions()]).then(([reference, offered]) => {
      if (!mounted.yes) return;
      if (reference.ok) setMakes(reference.value);
      if (offered.ok) setOptions(offered.value);
    });
    return () => {
      mounted.yes = false;
    };
  }, [listVehicleMakes, listVehicleOptions]);

  const chosenCity = cities.find((entry) => entry.name === (champs["city"] ?? ""));
  useEffect(() => {
    if (chosenCity === undefined) {
      setPlaces([]);
      return;
    }
    /** A reply for a city already left is stale: the last city CHOSEN wins, not the last answer to ARRIVE. */
    const arrival = { current: true };
    void (async () => {
      const answer = await listCityZones(chosenCity.id);
      if (arrival.current) setPlaces(answer.ok ? answer.value : []);
    })();
    return () => {
      arrival.current = false;
    };
  }, [chosenCity, listCityZones]);

  const set = (key: string) => (value: string) => {
    setChamps((current) => ({ ...current, [key]: value }));
  };

  /**
   * The eight named slots, and what a person has dropped into each.
   *
   * Held in the panel and uploaded at publish, because **a photograph needs a
   * car to belong to** , 06 §6.3.3's ticket names its owner at creation. That
   * is the whole reason `published_at` exists: create, attach, publish, in one
   * gesture the person never sees the seams of.
   */
  const [chosen, setChosen] = useState<Readonly<Record<string, File>>>({});
  /**
   * The ORDER they were chosen in, because the first one becomes the advert's
   * thumbnail and the drawing numbers each slot as it is filled.
   *
   * An object's key order would nearly work and would be a rule nobody wrote
   * down. A list says it.
   */
  const [order, setOrder] = useState<readonly string[]>([]);
  /** Which views have already reached the store, so a resume skips them. */
  const [sentViews, setSentViews] = useState<readonly string[]>([]);
  /** The equipment chips a person has turned on, in the order they turned them. */
  const [chosenEquipment, setChosenEquipment] = useState<readonly string[]>([]);
  /**
   * `round F5`: the door's refusal WITH the field it named, so the sentence can
   * stand beside the control a person must change rather than at the top of a
   * form eleven fields long.
   */
  const [refusal, setRefusal] = useState<{ field: string; message: string } | undefined>(undefined);
  /** The suggestions and the bounds, from the api that enforces them. */
  const [equipment, setEquipment] = useState<EquipmentReference>(NO_EQUIPMENT);

  useEffect(() => {
    let alive = true;
    void listEquipment().then((answer) => {
      if (alive && answer.ok) setEquipment(answer.value);
    });
    return () => {
      alive = false;
    };
  }, [listEquipment]);
  /**
   * The views, from the api that owns them.
   *
   * Empty until the door answers , eight slots drawn from a guess would be
   * eight slots that flicker into different names, and a panel with no slots
   * for one paint reads as a panel still loading, which is what it is.
   */
  const [views, setViews] = useState<PhotoViews>(NO_VIEWS);

  useEffect(() => {
    let alive = true;
    void listPhotoViews().then((answer) => {
      if (alive && answer.ok) setViews(answer.value);
    });
    return () => {
      alive = false;
    };
  }, [listPhotoViews]);
  /** Which view failed, so the panel can say WHICH and offer that one again. */
  const [failedView, setFailedView] = useState<string | null>(null);
  /**
   * `round F3`, `W2`: **the car this panel has already created.**
   *
   * The founder pressed « Mettre en ligne » twice on staging and got two cars,
   * `V-002` and `V-003`, neither with a picture. The gesture is create, upload,
   * publish; the first step had succeeded both times and only the second was
   * failing, so the second press created a second car before failing in the
   * same place.
   *
   * Held here, the id turns the second press into a RESUME: the car exists, so
   * the panel skips creation and carries on from the photograph that failed.
   * **A dealership's parc is not a place to leave debris**, and a duplicate row
   * is debris nobody can distinguish from a car somebody meant to list twice.
   */
  const [createdId, setCreatedId] = useState<string | null>(null);

  const mettreEnLigne = async (): Promise<void> => {
    if (enCours) return;
    setEnCours(true);
    setFailedView(null);
    try {
      /**
       * **Created once, and once only.**
       *
       * A second press finds the id from the first and goes straight to the
       * photographs. A car whose creation succeeded is not created again for
       * any reason: not a failed upload, not a refused publish, not a person
       * pressing twice because nothing seemed to happen.
       */
      let created = createdId;
      if (created === null) {
        const answer = await createVehicle({
          stockReference: champs["stockReference"] ?? "",
          make: champs["make"] ?? "",
          model: champs["model"] ?? "",
          year: Number(champs["year"] ?? ""),
          mileageKm: Number(champs["mileageKm"] ?? ""),
          priceAmount: Number(champs["priceAmount"] ?? ""),
          plate: champs["plate"] ?? "",
          // `S4-3`: a real boolean or nothing at all, never a guessed default ,
          // an empty selection must reach the door as UNANSWERED and be
          // refused there, exactly as a blank select would be for any other
          // required field.
          ...(champs["registrationDeclared"] === "oui"
            ? { registrationDeclared: true, registrationNumber: champs["registrationNumber"] ?? "" }
            : champs["registrationDeclared"] === "non"
              ? { registrationDeclared: false }
              : {}),
          colour: champs["colour"] ?? "",
          city: champs["city"] ?? "",
          district: champs["district"] ?? "",
          ...(champs["gearbox"] === undefined || champs["gearbox"] === ""
            ? {}
            : { gearbox: champs["gearbox"] }),
          ...(champs["fuel"] === undefined || champs["fuel"] === "" ? {} : { fuel: champs["fuel"] }),
          ...(champs["seats"] === undefined || champs["seats"] === ""
            ? {}
            : { seats: Number(champs["seats"]) }),
          // Always sent, empty included: absent and empty are the same fact and
          // the column stores them the same way. Sending nothing would leave
          // the door to guess which one a caller meant.
          equipment: chosenEquipment,
        });
        if (!answer.ok) {
          // The api's own sentence. The panel STAYS OPEN holding every field: a
          // refusal that closed it would make a dealer retype eleven fields to
          // correct one.
          //
          // `round F5`: and where the door named a FIELD, the sentence goes
          // beside that field as well , `4.5.5` sends it and the front used to
          // drop it, which is how a `400` about a quartier came to be read at
          // the top of a form.
          setErreur(answer.message);
          setRefusal(
            answer.field === undefined ? undefined : { field: answer.field, message: answer.message },
          );
          return;
        }
        created = answer.value.id;
        setCreatedId(created);
      }
      setErreur(null);

      /**
       * **Create, upload each view, publish** , and if an upload fails the car
       * stays UNPUBLISHED and the panel says which view failed.
       *
       * The order is forced by 06 §6.3.3: a ticket names its owner, so the car
       * must exist first. What the person sees is one gesture; what happens is
       * three kinds of call, and the failure mode that matters is the middle
       * one. A car half-photographed and published would be an advert a buyer
       * judges on two pictures; a car half-photographed and UNPUBLISHED is a
       * draft its owner can finish.
       */
      /**
       * **Only the views still missing**, so a resume does not re-upload what
       * already arrived. A photograph sent twice is two rows on one car, and
       * the view's partial unique index would refuse the second , correctly,
       * and with a message about a constraint that would tell nobody anything.
       */
      for (const [view, file] of Object.entries(chosen)) {
        if (sentViews.includes(view)) continue;
        const sent = await uploadPhoto({ vehicleId: created, view, file });
        if (!sent.ok) {
          setFailedView(view);
          setErreur(sent.message);
          return;
        }
        setSentViews((already) => [...already, view]);
      }

      const published = await publishVehicle(created);
      if (!published.ok) {
        // `WF-DLR-02`'s « at least one photo », in the api's own sentence. The
        // car exists and is not lost; the panel stays open holding everything.
        setErreur(published.message);
        return;
      }
      onListed();
    } finally {
      setEnCours(false);
    }
  };

  /** How many slots hold a file , the counter beside the title. */
  const chosenCount = order.length;

  const choose = useCallback((key: string, file: File): void => {
    setChosen((held) => ({ ...held, [key]: file }));
    setOrder((held) => (held.includes(key) ? held : [...held, key]));
    setFailedView(null);
  }, []);

  return (
    /**
     * `round F3`, `W5`: **`autoComplete="off"` on the whole panel.**
     *
     * The founder's browser autofilled an ADDRESS into it , a browser reads
     * « Ville », « Quartier » and a text field beside them and offers what it
     * has, which here is a person's own home. Nothing on this form is about the
     * person filling it in: every field describes a car. Turning it off is the
     * only honest answer, and it is set once on the form rather than field by
     * field, where the next field added would miss it.
     */
    <form
      className="space-y-3"
      data-testid="vehicule-formulaire"
      autoComplete="off"
      onSubmit={(event) => {
        // The gesture is a button, not a submit: an ENTER in a text field must
        // not create a car while eleven other fields are still empty.
        event.preventDefault();
      }}
    >
      {/**
       * **The photographs FIRST**, as the drawing has it: eight named slots in
       * a grid, then « Autres photos », then the eleven fields.
       *
       * The order is the seller's, not the form's. He is standing beside the
       * car with a phone; the fields are things he can type indoors, and the
       * photographs are the thing he can only do here.
       *
       * `round F3`, `W3`: the control itself lives in `photo-slots.tsx`, because
       * the car's PAGE needs the same one , a car born without pictures had no
       * way to receive any, and both of the founder's cars were born that way.
       */}
      <PhotoSlots
        views={views}
        content={Object.fromEntries(
          order.map((key) => [key, { file: chosen[key], rank: order.indexOf(key) + 1 }]),
        )}
        failedView={failedView}
        busyView={null}
        count={chosenCount}
        onChoose={choose}
        onRemove={(key) => {
          /**
           * `round F3b`, the founder's walk: **an × on every filled slot.**
           *
           * In the PANEL nothing has been sent yet , the car does not exist ,
           * so this clears the slot and nothing more. A person who chose the
           * wrong photograph for « Arrière » had, until now, only the option of
           * choosing another one; there was no way to say « none ».
           */
          setChosen((held) => {
            const kept: Record<string, File> = {};
            for (const [view, file] of Object.entries(held)) {
              if (view !== key) kept[view] = file;
            }
            return kept;
          });
          setOrder((held) => held.filter((view) => view !== key));
          setSentViews((already) => already.filter((view) => view !== key));
          if (failedView === key) setFailedView(null);
        }}
      />

      {/**
       * `P0-DLW-032`, round F4: **the fields are ONE component now.**
       *
       * « Modifier » on the car's page opens this same block, so the add form
       * and the edit form cannot disagree about what a car has. What differs is
       * the price , asked here, refused there , and the reference, which
       * freezes at publication.
       */}
      <VehicleFields
        values={champs}
        mode="create"
        vocabulary={{ makes, options, equipment, cities, places }}
        chosenEquipment={chosenEquipment}
        onToggleEquipment={(entry) => {
          setChosenEquipment((current) =>
            current.includes(entry) ? current.filter((kept) => kept !== entry) : [...current, entry],
          );
        }}
        onChange={(key, value) => {
          set(key)(value);
        }}
        // `round F5`: the door's own sentence, shown beside the field it names.
        refusal={refusal}
      />

      <div className="flex items-center gap-2 pt-1">
        {/**
         * `round F5`: **the gesture waits for the place to be whole.**
         *
         * The founder pressed a save with the quartier unchosen and read a
         * `400`. A screen may not offer a gesture the door will refuse: the
         * rule is `placeIsComplete`, owned by the field component, and the
         * two forms ask it rather than each keeping their own idea of it.
         *
         * `FX-2a`, `M1` (`F1`): **and it must SAY so.** It waited in a
         * `Button`'s `inactive`, whose own documentation reserves that word for
         * the client app , *« WF-CLI-01's "the control is simply inactive".
         * CLIENT surfaces only. »* , while `I19`'s scope line names « the
         * console, the dealer space and the bank app ». The dealer space reached
         * for the buyer's word, and the founder met the result: a dead
         * « Mettre en ligne » explaining nothing, on a form he had filled.
         *
         * What he had NOT filled was Profil, and that is why the reason names it
         * rather than only the two fields: the city and the quartier are
         * PROPOSED here from `GET /api/v1/me/dealership`, so an empty Profil
         * greys this control on every car a dealership adds, and the sentence
         * has to be true both for the person half-way through the form and for
         * the person whose dealership has no address at all.
         */}
        {/**
         * `FX-2a`, `M2` (`F2`): **and the SECONDS are said out loud.**
         *
         * `enCours` was set across the whole gesture , create, upload each view,
         * publish , read exactly once as `if (enCours) return` and rendered
         * NOWHERE. The founder pressed and sat in front of a control that read
         * what it had read before the press and still looked pressable.
         *
         * The outcome of the second press he then made is now established from
         * the wire rather than guessed (`TR-1` left it open): it is IGNORED, and
         * the car is created once , that guard and `createdId` both hold. So the
         * label says the work is happening, where a person is looking, and the
         * reason answers the question the wait actually raises.
         */}
        {enCours ? (
          <GuardedButton
            disabled
            reason={t("dealer.vehicules.mettreEnLigneEnCoursMotif")}
            testId="vehicule-mettre-en-ligne"
          >
            {t("dealer.vehicules.mettreEnLigneEnCours")}
          </GuardedButton>
        ) : placeIsComplete(champs, places) ? (
          <GuardedButton
            onClick={() => {
              void mettreEnLigne();
            }}
            testId="vehicule-mettre-en-ligne"
          >
            {t("dealer.vehicules.mettreEnLigne")}
          </GuardedButton>
        ) : (
          <GuardedButton
            disabled
            reason={t("dealer.vehicules.mettreEnLigneMotif")}
            testId="vehicule-mettre-en-ligne"
          >
            {t("dealer.vehicules.mettreEnLigne")}
          </GuardedButton>
        )}
        <Button variant="secondary" onClick={onCancel} data-testid="vehicule-annuler">
          {t("common.action.annuler")}
        </Button>
      </div>

      {/**
       * `FX-2a`, `M1`, **second reading, and the FRAME is what found it.**
       *
       * `M1` cured the silence with a `GuardedButton`, which satisfies `I19` by
       * type and carries the reason in the control's `title` and an `sr-only`
       * sentence. Then the pair was shot, and the frame showed a greyed button
       * with **nothing beside it** , because `round F3`'s `W5` narrowed `I19` to
       * put the reason in the tooltip after the founder asked for his fiche back
       * from two buttons each trailing a paragraph.
       *
       * That narrowing wrote down its own cost: *« What is lost is a reader who
       * cannot hover, and that is the cost the ruling accepts. It is written here
       * so the next round that touches `I19` finds the trade rather than the
       * result of it. »* **`F1` IS that cost, landing.** The founder did not
       * hover; he hunted, and found Profil by accident.
       *
       * So the trade is reopened HERE and nowhere else: `GuardedButton` is
       * untouched, the fiche he asked back stays as it is, and this one control ,
       * a panel's primary action, waiting on a fact set on ANOTHER SCREEN , says
       * why in words a camera and a reader can both see, with the way there.
       */}
      {placeIsComplete(champs, places) ? null : (
        <p className="text-sm text-gray-500" data-testid="vehicule-mettre-en-ligne-motif">
          {t("dealer.vehicules.mettreEnLigneMotif")}{" "}
          {naviguer === undefined ? null : (
            <button
              type="button"
              className={`font-medium ${LIEN_TEXTE}`}
              onClick={() => {
                naviguer("/profil");
              }}
              data-testid="vehicule-mettre-en-ligne-profil"
            >
              {t("dealer.vehicules.mettreEnLigneProfil")}
            </button>
          )}
        </p>
      )}

      {erreur === null ? null : (
        <p role="alert" className="text-sm text-rose-600" data-testid="vehicule-erreur">
          {erreur}
        </p>
      )}

      {/**
       * `round F3`, `W2`: **« Réessayer », for the view that failed and for
       * nothing else.**
       *
       * The gesture is not « try the whole thing again »: the car is created,
       * the photographs before this one are in the store, and repeating them
       * would be refused by the view's own unique index. It resumes , which
       * is why it is the same handler, and why the handler skips what is
       * already sent.
       *
       * It names the VIEW, because a person who chose eight photographs needs
       * to know which one the store would not take, and because a failure
       * that names nothing is a failure a person answers by pressing harder.
       */}
      {failedView === null || createdId === null ? null : (
        <div
          className="rounded-xl border border-rose-100 bg-rose-50 px-3 py-2.5"
          data-testid="vehicule-reessayer-bloc"
        >
          <p className="text-xs text-rose-700">
            {t("dealer.vehicules.photoEchouee")} « {failedView} »
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                void mettreEnLigne();
              }}
              data-testid="vehicule-reessayer"
            >
              {t("dealer.vehicules.reessayer")}
            </Button>
            <button
              type="button"
              onClick={() => {
                /**
                 * **Give this view up and go on.** A seller standing on a
                 * forecourt with one photograph the store will not take should
                 * not be held there: the car is created and one photograph is
                 * enough to publish, so dropping the slot lets the rest through.
                 */
                setChosen((held) => {
                  const kept: Record<string, File> = {};
                  for (const [key, file] of Object.entries(held)) {
                    if (key !== failedView) kept[key] = file;
                  }
                  return kept;
                });
                setOrder((held) => held.filter((key) => key !== failedView));
                setFailedView(null);
                setErreur(null);
              }}
              className="text-xs font-medium text-gray-500 hover:text-gray-700"
              data-testid="vehicule-abandonner-vue"
            >
              {t("dealer.vehicules.abandonnerVue")}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

/**
 * One named slot: a click opens the picker, and what comes back is shown IN
 * the slot.
 *
 * The preview matters more than it looks. The drawing shows the photograph in
 * its slot, and a slot that merely went green would leave a person who chose
 * eight files wondering which one landed where , which is exactly the mistake
 * a wrong « intérieur avant » makes expensive later.
 */
